"use client";
import React, { useMemo, useState } from "react";
import { Input, Spin, Tooltip } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import "./PrescriptionHistoryCard.css";

const IconWrap = ({ children, className = "", ...props }) => (
  <svg
    viewBox="0 0 24 24"
    className={className}
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    {...props}
  >
    {children}
  </svg>
);

// Icône ordonnance
const PrescriptionIcon = () => (
  <IconWrap className="phc-icon">
    <path
      d="M8 2h8a2 2 0 012 2v16l-6-3-6 3V4a2 2 0 012-2z"
      stroke="currentColor"
      strokeWidth="1.4"
      fill="none"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M10 7h4M10 11h4"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
    />
  </IconWrap>
);

export default function PrescriptionHistoryCard({
  title,
  subtitle,
  items = [],
  loading = false,
  onSeeAll,
  onSearchChange,
  onPrescriptionClick,
  placeholder,
  showTooltip = true,
}) {
  const { t } = useTranslation();

  // Defaults traduits (si props non fournies)
  const _title = title ?? t("prescriptionHistoryCard.ui.title");
  const _subtitle = subtitle ?? t("prescriptionHistoryCard.ui.subtitle");
  const _placeholder = placeholder ?? t("prescriptionHistoryCard.ui.searchPlaceholder");

  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((it) => (it?.name || "").toLowerCase().includes(q));
  }, [query, items]);

  const handleChange = (e) => {
    const v = e.target.value || "";
    setQuery(v);
    onSearchChange?.(v);
  };

  const handleItemClick = (prescription) => {
    if (onPrescriptionClick) onPrescriptionClick(prescription.id);
  };

  return (
    <div className="phc-card">
      {/* HEADER */}
      <div className="phc-head">
        <div className="phc-titles">
          <h3 className="phc-title">{_title}</h3>
          {_subtitle ? <div className="phc-subtitle">{_subtitle}</div> : null}
        </div>

        {/* Optionnel: bouton "See all" si tu l’utilises */}
        {typeof onSeeAll === "function" ? (
          <button className="phc-seeall" onClick={onSeeAll} type="button">
            {t("prescriptionHistoryCard.ui.seeAll")}
          </button>
        ) : null}
      </div>

      {/* SEARCH */}
      <div className="phc-search">
        <Input
          allowClear
          value={query}
          onChange={handleChange}
          placeholder={_placeholder}
          prefix={<SearchOutlined />}
          className="phc-search-input"
          disabled={loading}
        />
      </div>

      {/* LIST */}
      <div className="phc-list">
        {loading ? (
          <div className="phc-loading">
            <Spin size="large" />
            <p>{t("prescriptionHistoryCard.ui.loading")}</p>
          </div>
        ) : filtered.length > 0 ? (
          filtered.map((it) => (
            <div
              key={it.id}
              className={`phc-item ${onPrescriptionClick ? "phc-item-clickable" : ""}`}
              onClick={() => handleItemClick(it)}
              role={onPrescriptionClick ? "button" : undefined}
              tabIndex={onPrescriptionClick ? 0 : undefined}
              onKeyDown={(e) => {
                if (onPrescriptionClick && (e.key === "Enter" || e.key === " ")) {
                  handleItemClick(it);
                }
              }}
            >
              <div className="phc-left">
                <div className="phc-pill">
                  <PrescriptionIcon />
                </div>

                <div className="phc-info">
                  {/* Tooltip sur le nom */}
                  {showTooltip ? (
                    <Tooltip title={it.name} placement="topLeft" mouseEnterDelay={0.5}>
                      <div className="phc-name">{it.name}</div>
                    </Tooltip>
                  ) : (
                    <div className="phc-name">{it.name}</div>
                  )}

                  {/* Tooltip meta */}
                  <Tooltip
                    title={`${it.doctorName} • ${it.dateLabel}`}
                    placement="bottomLeft"
                    mouseEnterDelay={0.5}
                  >
                    <div className="phc-meta">
                      <span>{it.doctorName}</span>
                      <span className="phc-dot">•</span>
                      <span>{it.dateLabel}</span>
                    </div>
                  </Tooltip>
                </div>
              </div>

              <div className="phc-right">
                <span className="phc-type">{it.type}</span>
              </div>
            </div>
          ))
        ) : (
          <div className="phc-empty">
            {query
              ? t("prescriptionHistoryCard.ui.emptyFiltered")
              : t("prescriptionHistoryCard.ui.empty")}
          </div>
        )}
      </div>
    </div>
  );
}